import { FC, useCallback, useEffect, useRef, useState } from 'react';
import stylex from '@stylexjs/stylex';
import type { OptionInfo, RadiobuttonSubmissionData } from '@entities/leadCollector';
import type { RadiobuttonGroup } from '../lib/getRadiobuttonsFromBodyForm';
import { OptionGroup } from '../OptionGroup';
import { OptionItemRow } from '../OptionItemRow';
import { styles } from '../MainGuestTab/styles';

const RADIOBUTTON_REQUIRED_MESSAGE = 'Должно быть выбрано одно значение';

export interface RadiobuttonsFromFormBodyProps {
  groups: RadiobuttonGroup[];
  onChange?: (radiobuttons: RadiobuttonSubmissionData[]) => void;
  registerValidate?: (validate: () => Promise<boolean>) => void;
}

export const RadiobuttonsFromFormBody: FC<RadiobuttonsFromFormBodyProps> = ({ groups, onChange, registerValidate }) => {
  const [selectedByGroupId, setSelectedByGroupId] = useState<Record<string, { instanceId: string; text: string }>>({});
  const [errorsByGroupId, setErrorsByGroupId] = useState<Record<string, string>>({});
  const selectedByGroupIdRef = useRef(selectedByGroupId);
  selectedByGroupIdRef.current = selectedByGroupId;

  const onSelect = useCallback((groupInstanceId: string, option: { instanceId: string; text: string }) => {
    setSelectedByGroupId((prev) => ({ ...prev, [groupInstanceId]: option }));
    setErrorsByGroupId((prev) => {
      const next = { ...prev };
      delete next[groupInstanceId];
      return next;
    });
  }, []);

  const runValidation = useCallback(async (): Promise<boolean> => {
    if (!groups?.length) return true;
    const nextErrors: Record<string, string> = {};
    let valid = true;
    for (const group of groups) {
      const selected = selectedByGroupIdRef.current[group.instanceId];
      if (!selected?.text) {
        nextErrors[group.instanceId] = RADIOBUTTON_REQUIRED_MESSAGE;
        valid = false;
      }
    }
    setErrorsByGroupId(nextErrors);
    return valid;
  }, [groups]);

  useEffect(() => {
    registerValidate?.(runValidation);
    return () => registerValidate?.(undefined as unknown as () => Promise<boolean>);
  }, [registerValidate, runValidation]);

  useEffect(() => {
    if (!groups?.length) {
      onChange?.([]);
      return;
    }

    const result: RadiobuttonSubmissionData[] = [];
    for (const group of groups) {
      const selected = selectedByGroupId[group.instanceId];
      if (!selected?.text) continue;
      const titleRadiobutton = group.isTitleVisible && group.title ? group.title : undefined;
      result.push({
        titleRadiobutton,
        valueRadiobutton: [selected.text],
      });
    }
    onChange?.(result);
  }, [groups, selectedByGroupId, onChange]);

  if (!groups?.length) return null;

  return (
    <>
      {groups.map((group) => {
        const options = group.options ?? [];
        const selectedId = selectedByGroupId[group.instanceId]?.instanceId;
        const name = group.instanceId;
        const groupError = errorsByGroupId[group.instanceId];

        return (
          <OptionGroup
            key={group.instanceId}
            group={group}
            error={groupError}
            errorWrapperStyle={styles.radioGroupError}
          >
            {options.map((option: OptionInfo) => (
              <OptionItemRow key={option.instanceId} option={option}>
                <input
                  type="radio"
                  name={name}
                  value={option.instanceId}
                  checked={selectedId === option.instanceId}
                  onChange={() => onSelect(group.instanceId, { instanceId: option.instanceId, text: option.text })}
                  {...stylex.props(styles.radioInput)}
                />
              </OptionItemRow>
            ))}
          </OptionGroup>
        );
      })}
    </>
  );
};
